import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformApi, type ErpModule } from '@/api/platform.api';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setAccessibleModules, setEnabledModules } from '@/store/authSlice';
import { enabledHeadModuleCodes } from '@/config/moduleEntries';
import { notify } from '@/core/toast/notify';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { Button } from '@/components/ui/Button';
import { fattahProjectTitle } from '@/config/brand';
import {
  isFattahOneProductSelectionRequired,
  readOnboardingState,
  setProductSelectionDone,
  writeOnboardingState,
  type OnboardingModule,
} from './onboardingState';
import { resolveOwnerHomePath } from '@/utils/moduleAccess';

const moduleCatalog: { code: OnboardingModule; label: string; desc: string; erpModule: ErpModule }[] = [
  { code: 'POS', label: 'Point of Sale', desc: 'Billing, invoicing, and checkout', erpModule: 'POS' },
  { code: 'CRM', label: 'CRM', desc: 'Leads, deals, and customer lifecycle', erpModule: 'CRM' },
  { code: 'HR', label: 'HRMS', desc: 'Employees, attendance, and payroll', erpModule: 'HR' },
  { code: 'FINANCE', label: 'Finance', desc: 'Accounting and reporting', erpModule: 'FINANCE' },
  { code: 'PROJECTS', label: 'Projects', desc: 'Tasks, sprints, and delivery', erpModule: 'PROJECTS' },
  { code: 'DESK', label: 'Help Desk', desc: 'Support and ticket workflows', erpModule: 'DESK' },
  { code: 'SOCIAL', label: 'Social', desc: 'Publishing and inbox management', erpModule: 'SOCIAL' },
];

export function ProductSelectionPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const saved = readOnboardingState();
  const initial = useMemo(() => {
    const fromState = saved.selectedModules?.length ? saved.selectedModules : [];
    if (fromState.length > 0) return fromState;
    if (saved.intentModule) return [saved.intentModule];
    return ['POS'] as OnboardingModule[];
  }, [saved.intentModule, saved.selectedModules]);

  const [selected, setSelected] = useState<OnboardingModule[]>(initial);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isFattahOneProductSelectionRequired()) {
      navigate(resolveOwnerHomePath(enabledModules), { replace: true });
    }
  }, [enabledModules, navigate]);

  const toggle = (module: OnboardingModule) => {
    setSelected((prev) =>
      prev.includes(module) ? prev.filter((m) => m !== module) : [...prev, module],
    );
  };

  const continueSetup = async () => {
    if (selected.length === 0) {
      notify.error('Select at least one product to continue');
      return;
    }
    setSubmitting(true);
    try {
      await Promise.all(
        moduleCatalog.map((m) =>
          platformApi.updateModule(m.erpModule, selected.includes(m.code)),
        ),
      );
      const headCodes = enabledHeadModuleCodes(selected);
      dispatch(setEnabledModules(headCodes));
      const accessible = await platformApi.listAccessibleModules();
      if (accessible.length > 0) {
        dispatch(setAccessibleModules(accessible));
      } else {
        dispatch(setAccessibleModules(headCodes));
      }
      writeOnboardingState({
        ...saved,
        selectedModules: selected,
        step: 0,
      });
      setProductSelectionDone(true);
      notify.success('Products saved');
      navigate('/onboarding', { replace: true });
    } catch {
      notify.error('Could not save your product selection. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 dark:bg-slate-950">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <BrandLogo size="md" showName={false} />
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Welcome{user?.firstName ? `, ${user.firstName}` : ''} — choose the products you need
          </p>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900 sm:p-8">
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            {fattahProjectTitle('Choose products')}
          </h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Pick one or more apps for your organization. You can enable more later from settings.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {moduleCatalog.map((mod) => {
              const active = selected.includes(mod.code);
              return (
                <button
                  key={mod.code}
                  type="button"
                  onClick={() => toggle(mod.code)}
                  className={`rounded-2xl border p-4 text-left transition ${
                    active
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30'
                      : 'border-slate-200 hover:border-primary-300 dark:border-slate-700'
                  }`}
                >
                  <p className="font-semibold text-slate-900 dark:text-white">{mod.label}</p>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{mod.desc}</p>
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex justify-end">
            <Button
              type="button"
              onClick={() => void continueSetup()}
              disabled={submitting || selected.length === 0}
              className="min-w-[10rem]"
            >
              {submitting ? 'Saving…' : 'Continue setup'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
